import { Maybe } from "@figurepos/fgr-lib-backend/lib/types"
import { IsoDateTime } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"
import { AuditData } from "../Setting/types"

export interface CategoryAddPayload {
    categoryId: string
    locationId?: string
    parentCategoryId: Maybe<string>
    name: string
    shortName: Maybe<string>
    description: Maybe<string>
    icon: Maybe<number>
    color: Maybe<string>
    isActive: boolean
    taxesDisabled?: Maybe<boolean>
}

export interface CategoryAddRequestedPayload extends CategoryAddPayload {
    taxes?: Maybe<TaxPayload[]>
    pricings?: Maybe<PricingPayload[]>
    isActiveLocations?: IsActiveLocation[]
    auditData?: AuditData
}

export interface IsActiveLocation {
    id: string
    isActive: boolean
}

export interface CategoryUpdatedPayload {
    categoryId: string
    locationId?: string
    parentCategoryId?: string
    name?: string
    shortName?: string
    description?: string
    icon?: number
    color?: string
    isActive?: BooleanUntil
}

export interface BooleanUntil {
    value: boolean
    until?: IsoDateTime
}

export interface CategoryUpdateRequestedPayload extends CategoryUpdatedPayload {
    taxes?: Maybe<TaxPayload[]>
    pricings?: Maybe<PricingPayload[]>
    isActiveLocations?: IsActiveLocation[]
    auditData?: AuditData
}

export interface ItemOverrideData {
    price?: Maybe<number>
    optionGroup?: ItemOverrideOptionGroup
}

export interface ItemOverrideOptionGroup {
    id: string
    isActive: boolean
    options: ItemOverrideOption[]
}

export interface ItemOverrideOption {
    id: string
    price: number | undefined
}

export type Override<T> = Partial<T>

export interface ContextOverride<T> {
    context: ContextType
    contextId: string
    override: Override<T>
}

export type Overrides<T> = ContextOverride<T>[]

export enum ContextType {
    Location = "location",
    Terminal = "terminal",
}

export enum CategoryType {
    Category = "category",
    Product = "product",
    Option = "option",
    Modifier = "modifier",
}

export interface CategoryUpdatedTaxesPayload {
    categoryId: string
    locationId?: string
    taxes: TaxPayload[]
}

export interface TaxPayload {
    taxId: string
    isActive?: boolean
    isAutoApply?: boolean
    isEmbedded?: boolean
}

export interface CategoryUpdatedPricingsPayload {
    categoryId: string
    locationId?: string
    pricings: PricingPayload[]
}

export interface PricingPayload {
    pricingId: string
    isActive?: boolean
    isAutoApply?: boolean
}

export interface ProductAddRequestedPayload {
    productId: string
    locationId?: string
    parentCategoryId: Maybe<string>
    name: string
    displayName: Maybe<string>
    internalName: Maybe<string>
    kitchenName: Maybe<string>
    description: Maybe<string>
    barcode: Maybe<string>
    pluCode: Maybe<string>
    tare: Maybe<number>
    icon: Maybe<number>
    color: Maybe<string>
    isActive: boolean
    price: Maybe<number>
    unit: Maybe<string>
    pieces: Maybe<number>
    taxesDisabled: Maybe<boolean>
    alwaysOpenPopover: Maybe<boolean>
    containsAlcohol: Maybe<boolean>
    minQuantity: Maybe<number>
    maxQuantity: Maybe<number>
    disableItemNoteKiosk: Maybe<boolean>
    disableItemNoteMiB: Maybe<boolean>
    requireItemNoteMiB: Maybe<boolean>
    itemNoteMiBPlaceholder: Maybe<string>
    adjustablePricePOS?: Maybe<boolean>
    displayImagePOS?: Maybe<boolean>
    optionGroup: Maybe<OptionGroupAdded>
    modifierGroups: ModifierGroupAdded[]
    alternativeNames: Maybe<string[]>
    tags?: Maybe<string[]>
    cardProgramId: Maybe<string>
    images?: Maybe<CategoryImage[]>
    servicePriceOverrides: ServicePriceOverride[]
    taxes?: Maybe<TaxPayload[]>
    taxesLocations?: TaxesLocationsPayload[]
    pricings?: Maybe<PricingPayload[]>
    isActiveLocations?: IsActiveLocation[]
    auditData?: AuditData
}

interface ServicePriceOverride {
    serviceId: string
    price?: number
    optionGroup?: {
        id: string
        options: {
            id: string
            price: Maybe<number>
        }[]
    }
}

export interface ProductAddedPayload {
    productId: string
    locationId?: string
    parentCategoryId: string
    name: string
    displayName: Maybe<string>
    internalName: Maybe<string>
    kitchenName: Maybe<string>
    description: Maybe<string>
    barcode: Maybe<string>
    pluCode: Maybe<string>
    tare: Maybe<number>
    icon: Maybe<number>
    color: Maybe<string>
    isActive: boolean
    price: Maybe<number>
    unit: Maybe<string>
    pieces: Maybe<number>
    taxesDisabled: Maybe<boolean>
    alwaysOpenPopover: Maybe<boolean>
    containsAlcohol: Maybe<boolean>
    minQuantity: Maybe<number>
    maxQuantity: Maybe<number>
    disableItemNoteKiosk: Maybe<boolean>
    disableItemNoteMiB: Maybe<boolean>
    adjustablePricePOS?: Maybe<boolean>
    displayImagePOS?: Maybe<boolean>
    optionGroup: Maybe<OptionGroupAdded>
    modifierGroups?: ModifierGroupAdded[]
    alternativeNames: Maybe<string[]>
    tags?: Maybe<string[]>
    cardProgramId: Maybe<string>
    images?: Maybe<CategoryImage[]>
    taxesLocations?: TaxesLocationsPayload[]
}

export interface TaxesLocationsPayload {
    locationId: string
    taxes: TaxRecordPayload[]
}

export interface TaxRecordPayload {
    taxId: string
    isActive: boolean
}

export interface OptionGroupAdded {
    categoryGroupId: string
    isProminent: boolean
    options: OptionGroupItemAdded[]
}

export interface OptionGroupItemAdded {
    categoryGroupItemId: string
    price: number
    isPreset: boolean
    isActive?: boolean
    nestedCategoryGroupId?: Maybe<string>
}

export interface ModifierGroupAdded {
    categoryGroupId: string
    isProminent: boolean
    isExclusion: boolean
    modifiers: ModifierGroupItemAdded[]
}

export interface ModifierGroupItemAdded {
    categoryGroupItemId: string
    isPreset: boolean
    isActive?: boolean
    isMulti: boolean
    nestedCategoryGroupId?: Maybe<string>
}

export interface ProductOptionGroup {
    categoryGroupId: string
    categoryToCategoryGroupId: Maybe<string>
    previousCategoryToCategoryGroupId?: Maybe<string>
    options?: ProductOptionGroupItem[]
}

export interface ProductOptionGroupItem {
    categoryGroupItemId: string
    categoryGroupItemDetailId: string
    categoryGroupItemDetailLocationId: string
    price?: number
    isPreset?: boolean
}

export interface ProductUpdatedPayload {
    productId: string
    locationId?: string
    parentCategoryId?: string
    name?: string
    displayName?: string
    internalName?: string
    alternativeNames?: Maybe<string[]>
    kitchenName?: string
    tags?: Maybe<string[]>
    description?: string
    barcode?: string
    pluCode?: Maybe<string>
    tare?: Maybe<number>
    icon?: number
    color?: string
    isActive?: BooleanUntil
    price?: Maybe<number>
    unit?: Maybe<string>
    pieces?: Maybe<number>
    taxesDisabled?: Maybe<boolean>
    alwaysOpenPopover?: Maybe<boolean>
    containsAlcohol?: Maybe<boolean>
    minQuantity?: Maybe<number>
    maxQuantity?: Maybe<number>
    disableItemNoteKiosk?: Maybe<boolean>
    disableItemNoteMiB?: Maybe<boolean>
    adjustablePricePOS?: Maybe<boolean>
    displayImagePOS?: Maybe<boolean>
    optionGroup?: OptionGroupUpdated
    modifierGroups?: ModifierGroupUpdated[]
    images?: CategoryImage[]
    taxesLocations?: TaxesLocationsPayload[]
}

export interface OptionGroupUpdated {
    categoryGroupId: string
    isProminent?: boolean
    options?: OptionGroupItemUpdated[]
}

export interface OptionGroupItemUpdated {
    categoryGroupItemId: string
    price?: number
    isPreset?: boolean
    isActive?: boolean
    nestedCategoryGroupId?: Maybe<string>
}

export interface ModifierGroupUpdated {
    categoryGroupId: string
    isProminent?: boolean
    modifiers?: ModifierGroupItemUpdated[]
}

export interface ModifierGroupItemUpdated {
    categoryGroupItemId: string
    isPreset?: boolean
    isMulti?: boolean
    isActive?: boolean
    nestedCategoryGroupId?: Maybe<string>
}

export interface CategoryImage {
    id: string
    path: string
    type: Maybe<string>
    categoryId: string
    variants?: CategoryImageVariant[]
}

export enum ImageUploadCropType {
    Ratio1_1 = "1:1",
    Ratio2_1 = "2:1",
}

export interface CategoryImageVariant {
    id: string
    url: string
    croppedType: ImageUploadCropType
    cropCoords: {
        x: number
        y: number
        width: number
        height: number
    }
}

export interface ProductUpdateRequestedPayload {
    productId: string
    name: string
    displayName?: Maybe<string>
    internalName?: Maybe<string>
    description?: Maybe<string>
    kitchenName?: Maybe<string>
    alternativeNames?: Maybe<string[]>
    tags?: Maybe<string[]>
    parentId?: Maybe<string>
    isActive?: Maybe<boolean>
    icon?: Maybe<number>
    color?: Maybe<string>
    fixedPrice?: Maybe<number>
    minQuantity?: Maybe<number>
    maxQuantity?: Maybe<number>
    unit?: Maybe<string>
    tare?: Maybe<number>
    pieces?: Maybe<number>
    containsAlcohol?: Maybe<boolean>
    alwaysOpenPopover?: Maybe<boolean>
    disableItemNoteKiosk?: Maybe<boolean>
    disableItemNoteMiB?: Maybe<boolean>
    requireItemNoteMiB?: Maybe<boolean>
    itemNoteMiBPlaceholder?: Maybe<string>
    adjustablePricePOS?: Maybe<boolean>
    displayImagePOS?: Maybe<boolean>
    optionGroup?: Maybe<OptionGroupUpdated>
    modifierGroups?: ModifierGroupUpdated[]
    taxes?: Maybe<TaxPayload[]>
    pricings?: Maybe<PricingPayload[]>
    isCreating?: Maybe<boolean>
    locationId?: Maybe<string>
    pluCode?: Maybe<string>
    barcode?: string
    images?: Maybe<CategoryImage[]>
    isActiveLocations?: IsActiveLocation[]
    overrides: ContextOverride<ItemOverrideData>[]
    taxesLocations?: TaxesLocationsPayload[]
    auditData?: AuditData
}
